import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getPresence } from '../api/client'
import { useStore } from '../store'
import type { PresenceUser } from '../types'

const POLL_INTERVAL_MS = 30_000
const MAX_VISIBLE = 5

// Deterministische kleur op basis van gebruikersnaam
const AVATAR_COLORS = [
  'bg-blue-500',
  'bg-emerald-500',
  'bg-violet-500',
  'bg-orange-500',
  'bg-pink-500',
  'bg-teal-500',
  'bg-sky-500',
  'bg-rose-500',
]

function avatarColor(username: string): string {
  let hash = 0
  for (let i = 0; i < username.length; i++) {
    hash = (hash * 31 + username.charCodeAt(i)) >>> 0
  }
  return AVATAR_COLORS[hash % AVATAR_COLORS.length]
}

function avatarLabel(user: PresenceUser): string {
  if (user.initials) return user.initials.slice(0, 4).toUpperCase()
  return user.username.slice(0, 2).toUpperCase()
}

function avatarFontSize(label: string): string {
  if (label.length <= 2) return 'text-[10px]'
  if (label.length === 3) return 'text-[8px]'
  return 'text-[7px]'
}

function minutesAgo(isoString: string, t: (key: string, opts?: Record<string, unknown>) => string): string {
  const diff = Math.floor((Date.now() - new Date(isoString).getTime()) / 60_000)
  if (diff < 1) return t('presence_avatars.active_now')
  return t('presence_avatars.active_minutes_ago', { count: diff })
}

interface AvatarProps {
  user: PresenceUser
  isSelf: boolean
  zIndex: number
}

function Avatar({ user, isSelf, zIndex }: AvatarProps) {
  const { t } = useTranslation()
  const [hovered, setHovered] = useState(false)
  const color = avatarColor(user.username)
  const label = avatarLabel(user)
  const fontSize = avatarFontSize(label)

  return (
    <div
      className="relative"
      style={{ zIndex }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div
        className={`
          w-7 h-7 rounded-full flex items-center justify-center text-white font-bold
          cursor-default select-none transition-transform hover:scale-110
          ${fontSize} ${color}
          ${isSelf ? 'ring-2 ring-white ring-offset-1 ring-offset-white' : ''}
        `}
        title={t('presence_avatars.avatar_title', { username: user.username, time: minutesAgo(user.last_seen, t) })}
      >
        {label}
      </div>

      {hovered && (
        <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 z-50 pointer-events-none">
          <div className="w-2 h-2 bg-gray-900 rotate-45 mx-auto -mb-1" />
          <div className="bg-gray-900 text-white text-xs rounded-lg px-2.5 py-1.5 whitespace-nowrap shadow-lg">
            <div className="font-semibold">{user.username}</div>
            <div className="text-gray-300 mt-0.5">{minutesAgo(user.last_seen, t)}</div>
          </div>
        </div>
      )}
    </div>
  )
}

export function PresenceAvatars() {
  const { t } = useTranslation()
  const { currentUser } = useStore()
  const [users, setUsers] = useState<PresenceUser[]>([])

  useEffect(() => {
    let cancelled = false

    async function fetchPresence() {
      try {
        const data = await getPresence()
        if (!cancelled) setUsers(data)
      } catch {
        // stil falen — presence is niet kritiek
      }
    }

    fetchPresence()
    const id = setInterval(fetchPresence, POLL_INTERVAL_MS)
    return () => {
      cancelled = true
      clearInterval(id)
    }
  }, [])

  if (users.length === 0) return null

  const visible = users.slice(0, MAX_VISIBLE)
  const overflow = users.length - MAX_VISIBLE

  return (
    <div className="flex items-center" title={t('presence_avatars.users_active', { count: users.length })}>
      <div className="flex items-center" style={{ gap: '-4px' }}>
        {visible.map((u, i) => (
          <div key={u.username} style={{ marginLeft: i === 0 ? 0 : '-8px' }}>
            <Avatar
              user={u}
              isSelf={u.username === currentUser?.username}
              zIndex={MAX_VISIBLE - i}
            />
          </div>
        ))}

        {overflow > 0 && (
          <div
            className="w-7 h-7 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-[10px] font-bold text-gray-600 dark:text-gray-400 cursor-default select-none"
            style={{ marginLeft: '-8px', zIndex: 0 }}
            title={t('presence_avatars.overflow_title', { count: overflow })}
          >
            +{overflow}
          </div>
        )}
      </div>
    </div>
  )
}
